import { Tag } from 'antd';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import React, { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { config } from '../../constant';
import ReactEcharts from '../../Echarts/Echarts';
import { arrayOfObjects } from '../../../Mock/MockJson';
import { ErrorComponent, NoRecordComponent } from '../../Utils';
import { getChartDataWithoutSelection, getChartDataWithSelection } from '../../ChartCalls';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

const HeatmapCalendar = (props) => {
  const [option, setOption] = useState({});
  const [refreshDateTime, setRefreshDateTime] = useState(dayjs());
  const [errorInAPI, setErrorInAPI] = useState(false);
  const [noDataInDB, setNoDataInDB] = useState(false);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  let timeTicket = useRef(null);
  const requestCheck = useRef(true);
  const abortController = useRef(new window.AbortController());

  const {
    graphprops,
    selectionDate,
    selectionMonth,
    DeviceCode,
    selectionDateRange,
    dayMonthYear,
    currentTimeZone,
  } = props;

  useEffect(() => {
    if (timeTicket.current) {
      clearInterval(timeTicket.current);
    }
    let refreshTime = graphprops?.refreshTime ? graphprops.refreshTime * 60000 : config.defaultRefreshTime;
    timeTicket.current = setInterval(() => {
      if (requestCheck.current) {
        getData();
      }
    }, refreshTime);
    return () => {
      abortController.current.abort();
      clearInterval(timeTicket.current);
    };
  }, []);

  useEffect(() => {
    getData(props);
  }, [
    selectionDate,
    selectionMonth,
    selectionDateRange,
    DeviceCode,
    dayMonthYear,
    props.translationCache
  ]);




  const getData = useCallback(async () => {
    // Abort previous fetch if any
    abortController.current.abort();
    abortController.current = new AbortController();

    let json = [];
    let currentOption = getOption();
    setErrorInAPI(false);
    setNoDataInDB(false);
  if (graphprops && graphprops.enableMock && graphprops.enableMock === true) {
      json = arrayOfObjects;
      requestCheck.current = true;
    } else {
      if (graphprops?.graph === 'rangeBar' && graphprops?.currentDateData !== undefined) {
        json = graphprops.currentDateData;
      } else {
        if (
          graphprops &&
          ((graphprops.DashboardLevelSelection === 'datePicker' ||
            graphprops.GraphLevelSelection === 'datePicker') &&
            selectionDate) || 
          (
            (graphprops.DashboardLevelSelection === 'monthPicker' ||
              graphprops.GraphLevelSelection === 'monthPicker') &&
            selectionMonth) || 
          (
            (graphprops.DashboardLevelSelection === 'dateRangePicker' ||
              graphprops.GraphLevelSelection === 'dateRangePicker') &&
            selectionDateRange) || 
          (
            (graphprops.DashboardLevelSelection === 'dateMonthYearPicker' ||
              graphprops.GraphLevelSelection === 'dateMonthYearPicker') &&
            dayMonthYear) ||
          (
            (graphprops.DashboardLevelSelection === 'deviceSelection' ||
              graphprops.GraphLevelSelection === 'deviceSelection') &&
            DeviceCode)
        ) {
          json = await getChartDataWithSelection({
            graphprops,
            selectionDate,
            selectionMonth,
            selectionDateRange,
            DeviceCode,
            dayMonthYear,
            abortController: abortController.current,
            currentTimeZone,
          });
          requestCheck.current = true;
        } else if (
          graphprops.GraphLevelSelection === 'none' ||
          graphprops.DashboardLevelSelection === 'none'
        ) {
          json = await getChartDataWithoutSelection({
            graphprops,
            abortController: abortController.current,
            DeviceCode,
            TimeZone: currentTimeZone,
          });
          requestCheck.current = true;
        }
      }
    }
    if ((json && json.message) || (Array.isArray(json) && json.length === 0 && graphprops.enableMock !== true)) {
      if (json.message) {
        setErrorInAPI(true);
      } else if (json.length === 0) {
        setNoDataInDB(true);
      }
    } else {
      let seriesData = [];
      if (json && Array.isArray(json)) {
        seriesData = json.map(item => {
          const dateTimeInIST = dayjs.utc(item.DateTime).tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss');
          return item.Value ? [dateTimeInIST, item.Value] : [];
        });
        currentOption.series[0].data = seriesData;
        currentOption.series[1].data = seriesData;
        setOption(currentOption);
        setRefreshDateTime(dayjs());
      }
    }
  }, [graphprops, props]);


  const getOption = () => {
    let range = '';
    let { dateExists, monthExists } = graphprops;

    if (dateExists && dateExists === 'true') {
      range = dayjs(props.selectionDate).tz('Asia/Kolkata').format('YYYY-MM');
    } else if (monthExists && monthExists === 'true') {
      range = props.selectionMonth;
    }

    return {
      title: {
        top: 30,
        left: 'center'
      },
      toolbox: {
        showTitle: false,
        itemSize: 25,
        feature: {
          saveAsImage: {
            backgroundColor: props.theme.algorithm === "dark" ? 'black' : 'white',
            title: 'download',
            name: graphprops.title,
            iconStyle: {
              borderColor: '#8c8c8c',
              borderWidth: 2
            }
          }
        }
      },
      tooltip: {
        position: 'top',
        formatter: function (param) {
          return [param.marker, '<bold>' + param.value[0] + '</bold>', ': ' + param.data[1]].join(
            ''
          );
        }
      },
      visualMap: {
        min: 0,
        max: 10000,
        type: 'piecewise',
        orient: 'horizontal',
        left: 'center',
        top: 40,
        pieces:
          graphprops && graphprops[graphprops.graph] && graphprops[graphprops.graph].VisualMap
            ? graphprops[graphprops.graph].VisualMap
            : [],
        outOfRange: {
          color: '#999'
        },
        textStyle: {
          color: props.theme.algorithm === "dark" ? 'white' : 'black',
          fontWeight: 'bold',
          fontSize: 18
        }
      },
      calendar: {
        top: 120,
        left: 80,
        right: 20,
        cellSize: [70, 70],
        range: range || dayjs().tz('Asia/Kolkata').format('YYYY'),
        itemStyle: {
          borderWidth: 0.5
        },
        dayLabel: {
          color: props.theme.algorithm === "dark" ? 'white' : 'black',
          fontWeight: 'bold',
          fontSize: 18
        },
        monthLabel: {
          textStyle: {
            color: props.theme.algorithm === "dark" ? 'white' : 'black',
            fontWeight: 'bold',
            fontSize: 18
          },
          color: props.theme.algorithm === "dark" ? 'white' : 'black'
        },
        yearLabel: {
          show: true,
          margin: 50,
          color: props.theme.algorithm === "dark" ? 'white' : 'black',
          fontWeight: 'bold',
          fontSize: 20
        }
      },
      series: [
        {
          type: 'scatter',
          coordinateSystem: 'calendar',
          symbolSize: 1,
          data: [],
          label: {
            show: true,
            formatter: function (params) {
              var d = params.value[0];
              return d + '\n\n' + params.value[1];
            },
            color: '#000'
          }
        },
        {
          type: 'heatmap',
          coordinateSystem: 'calendar',
          symbolSize: 1,
          data: []
        }
      ]
    };
  };

  return (
    <Fragment>
      {graphprops && graphprops.checked === true ? (
        <Tag className="graphTag" color="blue">
          {refreshDateTime ? refreshDateTime.tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss') : ''}
        </Tag>
      ) : null}
      {noDataInDB || errorInAPI ? (
        noDataInDB ? (
          <NoRecordComponent />
        ) : (
          <ErrorComponent />
        )
      ) : (
        <ReactEcharts
          id={Math.ceil(Math.random(100, 100000) * 1000)}
          option={option}
          style={{
            height: '80%',
            width: '100%'
          }}
          className="react_for_echarts"
        />
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    theme: state.ThemeReducer,
    translationCache: state.TranslationReducer.translationCache,
    currentTimeZone: state.LayoutReducer.currentTimeZone
  };
};

export default connect(mapStateToProps)(HeatmapCalendar);